import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { useChartOfAccounts } from "@/features/ledger/api/use-chart-of-accounts";
import type { ChartOfAccountFilter } from "@/features/ledger/api/dto";
import {
  ChartOfAccountFormDialog,
  chartOfAccountsColumns,
  type ChartOfAccountRow,
} from "@/features/ledger/components";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import type { AccountType } from "@/features/shared/api/enums";

/** Traduit les filtres de colonnes DataTableClar en `ChartOfAccountFilter` (§4.2). */
const toChartOfAccountFilter = (
  filters: ColumnFiltersState
): ChartOfAccountFilter => {
  const filter: ChartOfAccountFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "code") filter.code = value;
    if (id === "label") filter.label = value;
    if (id === "accountType") filter.accountType = value as AccountType;
    if (id === "businessLine") filter.businessLine = value;
  }
  return filter;
};

/**
 * Plan de comptes paginé (`POST /ChartOfAccounts/all`, guide §6.4) —
 * lecture authentifiée ; écriture réservée à flashpay-superadmin /
 * flashpay-admin (désactivation : superadmin uniquement).
 */
export function ChartOfAccountsPage() {
  const { t } = useLedgerTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toChartOfAccountFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useChartOfAccounts(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("ledger.accounts.title")}
        </h1>
        <RequireRole
          roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
        >
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 w-4 h-4" />
            {t("ledger.accounts.create")}
          </Button>
        </RequireRole>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter((r): r is ChartOfAccountRow =>
          Boolean(r.id)
        )}
        columnsClar={chartOfAccountsColumns}
        title={t("ledger.accounts.title")}
        exportFileName="chart-of-accounts"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <ChartOfAccountFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
